import { ChangeDetectorRef, Component, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize, timeout } from 'rxjs';
import { RewardsCardError, RewardsCardErrorCode } from '../../../shared/interfaces/rewards-card';
import { RewardsCardService } from '../../../shared/services/rewards-card.service';

const UNLOCK_ERROR_MESSAGES: Record<RewardsCardErrorCode, string> = {
    invalid: 'Code not recognized. Please see a sales rep.',
    used: 'This code has already been used.',
    expired: 'This code has expired.',
    upstream_error: 'Unable to verify code. Please try again or see a sales rep.',
};

@Component({
    selector: 'app-rewards-card-unlock',
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule],
    templateUrl: './rewards-card-unlock.component.html',
    styleUrl: './rewards-card-unlock.component.scss',
})
export class RewardsCardUnlockComponent {
    public readonly codeControl = new FormControl<string>('', { nonNullable: true, validators: [Validators.required] });

    public submitting = false;

    public unlockError: string | null = null;

    @Output() public readonly unlocked = new EventEmitter<void>();

    public constructor(
        private readonly rewardsCards: RewardsCardService,
        private readonly cdr: ChangeDetectorRef,
    ) {
    }

    public submitCode(): void {
        if (this.submitting) {
            return;
        }

        this.codeControl.markAsTouched();
        if (this.codeControl.invalid) {
            return;
        }

        this.submitting = true;
        this.unlockError = null;

        this.rewardsCards.redeem(this.codeControl.value.trim()).pipe(
            timeout(30000),
            finalize(() => {
                this.submitting = false;
                this.cdr.markForCheck();
            }),
        ).subscribe({
            next: () => this.unlocked.emit(),
            error: (response: unknown) => {
                console.error('Rewards card redeem failed', response);
                const errorBody = response instanceof HttpErrorResponse
                    ? response.error as RewardsCardError | null
                    : null;
                this.unlockError = UNLOCK_ERROR_MESSAGES[errorBody?.error_code ?? 'upstream_error']
                    ?? UNLOCK_ERROR_MESSAGES.upstream_error;
            },
        });
    }
}
